// @flow
import React, { type Element } from 'react';
import { connect } from 'react-redux';
import BoardEditModal from 'app/common/react/components/BoardEdit/BoardEditModal';
import BoardFollowButton from 'app/common/react/components/BoardCard/BoardFollowButton';
import CONST from 'app/common/lib/Const';
import Credits from 'app/common/react/components/BoardCard/Credits';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PinCount from 'app/common/react/components/common/Count/PinCount';
import type { Image } from 'app/packages/pinterest-flow-types/image';
import type { ImagePositionType } from 'app/common/react/components/profile/ProfileBoardCard/commonTypes';
import { Button } from 'gestalt';
import { compose } from 'redux';
import { track } from 'app/common/lib/react/analytics';
import { type UnauthSeoExperiments } from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  hoverMaskStyle,
  boardNameDarkStyle,
  boardNameStyle,
  authorNameStyle,
  ownerAvatarStyle,
  namePinCountDarkStyle,
  namePinCountStyle,
} from 'app/common/react/components/BoardCard/BoardCardStyle';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';

const NUM_OF_THUMBS = 3;
const dummyThumbs = ['', '', ''];

export type CoverImageType = {
  height: number,
  url: string,
  width: number,
};

export type BoardPrivacyType =
  | typeof CONST.BoardPrivacy.PUBLIC
  | typeof CONST.BoardPrivacy.SECRET
  | typeof CONST.BoardPrivacy.PROTECTED;

export type BoardType = {
  access: Array<string>,
  collaborating_users: Array<{
    full_name: string,
    image_medium_url: string,
    is_default_image: boolean,
    username: string,
  }>,
  cover_images: { [key: string]: CoverImageType },
  cover_pin: {
    crop: Array<number>,
    size: Array<number>,
  },
  debug: boolean,
  description: string,
  followed_by_me: boolean,
  id: string,
  image_cover_hd_url: string,
  image_cover_url: string,
  images: { [key: string]: Image },
  is_collaborative: boolean,
  name: string,
  owner: {
    id: string,
    username: string,
    full_name: string,
    image_medium_url: string,
    blocked_by_me?: boolean,
  },
  pin_count: number,
  pin_thumbnail_urls: Array<string>,
  privacy: BoardPrivacyType,
  section_count: number,
  showAuthorName: boolean,
  url: string,
};

type State = {
  isEditModalOpen: boolean,
  isFollowButtonLoadingShown: boolean,
  coverImgPos: number,
  coverImgUrl: string,
  hoveredOverCredits: boolean,
};

type OwnProps = {|
  board: BoardType,
  className?: string,
  credits?: {
    link: ?string,
    name: string,
    thumbnailAlt: string,
    thumbnailSrc: string,
    title: string,
    creditText: string,
  },
  handleClickLogging?: Function,
  isDarkCard?: boolean,
  isUnauthProfileLanding?: boolean,
  onBoardDelete?: Function,
  onBoardPrivacyChange?: Function,
  showAuthorProfile?: boolean,
  showFooterButton?: boolean,
  seoUnauthExperiments?: UnauthSeoExperiments,
|};

type HOCProps = {|
  requestContext: RequestContextType,
|};

type ReduxProps = {|
  pinCount: number,
|};

type Props = {|
  ...OwnProps,
  ...HOCProps,
  ...ReduxProps,
|};

export class BoardCard extends React.Component<Props, State> {
  static defaultProps = {
    showFooterButton: true,
  };

  state: State = {
    isEditModalOpen: false,
    isFollowButtonLoadingShown: false,
    coverImgPos: 0,
    coverImgUrl: '',
    hoveredOverCredits: false,
  };

  getCoverImgUrl: () => string = () => {
    if (this.state.coverImgPos) {
      return this.state.coverImgUrl;
    }

    const {
      image_cover_hd_url: imageCoverHdUrl,
      image_cover_url: imageCoverUrl,
    } = this.props.board;
    return imageCoverHdUrl || imageCoverUrl;
  };

  props: Props;

  handleBoardCardClick: () => void = () => {
    const { board, handleClickLogging } = this.props;
    // TODO (michelle): Discuss interface for context logging. It may be better
    // to pass context log params as props instead of a callback.
    if (handleClickLogging) {
      handleClickLogging();
    } else {
      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.FLOWED_BOARD,
        element: PCONST.thrift.ElementType.BOARD_COVER,
        objectId: board.id,
        view: PCONST.thrift.ViewType.USER,
      });
    }
  };

  handleCropChange: (coverImgUrl: string, coverImgPos: ImagePositionType) => void = (
    coverImgUrl,
    coverImgPos,
  ) => {
    this.setState({
      coverImgUrl,
      coverImgPos: -coverImgPos.y,
    });
  };

  handleEditClose: () => void = () => {
    this.setState({ isEditModalOpen: false });
  };

  handleEdit: () => void = () => {
    if (!this.props.board) {
      return;
    }

    const { board } = this.props;

    this.setState({ isEditModalOpen: true });

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.FLOWED_BOARD,
      element: PCONST.thrift.ElementType.BOARD_EDIT_BUTTON,
      objectId: board.id,
      view: PCONST.thrift.ViewType.USER,
    });
  };

  handleCreditEnter = () => this.setState({ hoveredOverCredits: true });

  handleCreditLeave = () => this.setState({ hoveredOverCredits: false });

  props: Props;
  followSubscriber: any;
  unfollowSubscriber: any;

  isUserHasAccess: () => boolean = () => (this.props.board.access || []).indexOf('write') !== -1;

  renderFooterButton = () => {
    const { board } = this.props;
    if (this.isUserHasAccess()) {
      return <Button onClick={this.handleEdit} text={i18n._('Edit')} />;
    } else {
      const coverImgUrl = this.getCoverImgUrl();
      return board.owner ? (
        <BoardFollowButton
          boardId={board.id}
          boardUrl={board.url}
          boardImage={coverImgUrl}
          boardName={board.name}
          isFollowed={board.followed_by_me}
          ownerId={board.owner.id}
        />
      ) : null;
    }
  };

  renderThumbs: (thumbnailUrls: Array<string>) => Array<Element<any>> = (thumbnailUrls) => {
    const renderedThumbs = [];

    for (let i = 0; i < NUM_OF_THUMBS; i += 1) {
      renderedThumbs.push(
        <li key={i}>
          <span className="hoverMask" style={hoverMaskStyle} />
          {thumbnailUrls[i] ? <img className="thumb" alt="" src={thumbnailUrls[i]} /> : null}
        </li>,
      );
    }

    return renderedThumbs;
  };

  render() {
    const { board } = this.props;

    if (!board) {
      return null;
    }
    const {
      name,
      description,
      is_collaborative,
      owner,
      pin_thumbnail_urls,
      privacy,
      url,
      showAuthorName,
    } = board;

    let pinThumbUrls = pin_thumbnail_urls;

    const {
      className,
      credits,
      isDarkCard,
      isUnauthProfileLanding,
      onBoardDelete,
      onBoardPrivacyChange,
      pinCount,
      showAuthorProfile,
      showFooterButton,
    } = this.props;

    const { hoveredOverCredits } = this.state;

    const altText = name + (description ? ' / ' + description : '');

    if (!pinThumbUrls || !pinThumbUrls.length) {
      pinThumbUrls = dummyThumbs;
    }

    const coverImgUrl = this.getCoverImgUrl();
    const coverImgStyles = {
      backgroundImage: `url(${coverImgUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      height: '100%',
    };

    return (
      <div className="item">
        <div
          className={cx('Board Module boardCoverImage', className, {
            boardWrapperDisableHover: hoveredOverCredits,
          })}
        >
          {/* TODO(bca): handle deeplinking */}
          <NavigateLink
            className="boardLinkWrapper"
            onClick={this.handleBoardCardClick}
            to={url}
            forceRefresh={PCONST.ForceRefreshType.PAGE}
          >
            <div className={cx('boardName', { hasBoardContext: showAuthorProfile })}>
              <div className="BoardIcons Module pinCreate">
                {is_collaborative ? <span className="collaborativeIcon" /> : null}
                {privacy === 'secret' ? <span className="secretIcon" /> : null}
              </div>
              {privacy === 'secret' ? <span className="secretIcon" /> : null}
              <div className="name" style={isDarkCard ? boardNameDarkStyle : boardNameStyle}>
                {name}
              </div>
              {(showAuthorProfile || showAuthorName) && owner ? (
                <div className="authorName" style={authorNameStyle}>
                  <div style={isDarkCard ? namePinCountDarkStyle : namePinCountStyle}>
                    {owner.full_name}
                    {owner.full_name.length < 24 && (
                      <span>
                        <span>{' • '}</span> <PinCount count={pinCount} />
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="boardPinCount">
                  <PinCount count={pinCount} />
                </div>
              )}
            </div>
            {isUnauthProfileLanding ? (
              <div className="boardImagesWrapper">
                <div className="boardCoverWrapper">
                  <ul className="boardThumbs">{this.renderThumbs(pinThumbUrls.slice(0, 2))}</ul>
                </div>
                <ul className="boardThumbs">{this.renderThumbs(pinThumbUrls.slice(2))}</ul>
                {showAuthorProfile &&
                  owner && (
                    <img
                      alt={`More from ${owner.full_name}`}
                      src={owner.image_medium_url}
                      style={ownerAvatarStyle}
                    />
                  )}
              </div>
            ) : (
              <div className="boardImagesWrapper">
                <div className="boardCoverWrapper">
                  <span className="hoverMask" style={hoverMaskStyle} />
                  {coverImgUrl ? (
                    <div role="img" aria-label={altText} title={altText} style={coverImgStyles} />
                  ) : null}
                </div>
                <ul className="boardThumbs">{this.renderThumbs(pinThumbUrls)}</ul>
                {showAuthorProfile &&
                  owner && (
                    <img
                      alt={`More from ${owner.full_name}`}
                      src={owner.image_medium_url}
                      style={ownerAvatarStyle}
                    />
                  )}
              </div>
            )}
          </NavigateLink>
          {showFooterButton &&
            this.props.requestContext.isAuthenticated &&
            this.renderFooterButton()}
          {this.state.isEditModalOpen && (
            <BoardEditModal
              boardId={board.id}
              onBoardDelete={onBoardDelete}
              onBoardPrivacyChange={onBoardPrivacyChange}
              onClose={this.handleEditClose}
              onCropChange={this.handleCropChange}
            />
          )}
          {credits && (
            <Credits
              credits={credits}
              onMouseEnter={this.handleCreditEnter}
              onMouseLeave={this.handleCreditLeave}
            />
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ boards }, props: OwnProps): ReduxProps => {
  // we should attempt to use the updated pin counts from the redux store
  // however, if the board does not exist in the redux store, fall back on resource board pin count
  const reduxBoard = ((boards || {}).content || {})[props.board.id];
  const pinCount = (reduxBoard && reduxBoard.pin_count) || props.board.pin_count || 0;

  return { pinCount };
};

export default compose(
  withRequestContext,
  connect(mapStateToProps),
)(BoardCard);
